import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown, FaSearch, FaTimes } from "react-icons/fa";
import SearchSelectDropdown, {
  SEARCH_SELECT_INPUT_CLASS,
} from "../TaskManagement/SearchSelectDropdown";
import { countries_code } from "../../utilis/constants";
import { buildActiveFilterChips, buildCounselingFilterOptions, mapApiFilterOptions } from "./counselingFilterUtils";
import "../../styles/tailwind.css";

const CMS_PRIMARY = "#007BFF";

const FILTER_BUTTON_RESET = {
  border: "none",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
};

const TRIGGER_RESET_STYLE = {
  boxShadow: "none",
  WebkitAppearance: "none",
  appearance: "none",
};

const LABEL_CLASS =
  "mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500";

const ROW_CLASS = "counseling-filter-row";
const COUNSELING_MENU_CLASS = "counseling-filter-menu z-[200]";

function MultiSearchSelectDropdown({
  values = [],
  onChange,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  emptyLabel = "No options available",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const selectedValues = useMemo(
    () => values.map((item) => String(item.value ?? item.id ?? item)),
    [values],
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      String(option.label || "")
        .toLowerCase()
        .includes(query),
    );
  }, [options, search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      setSearch("");
    }
  }, [open]);

  const selectedLabel =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? values[0].label || values[0].name
        : `${values.length} selected`;

  const toggleOption = (option) => {
    const exists = selectedValues.includes(String(option.value));
    if (exists) {
      onChange(
        values.filter(
          (item) => String(item.value ?? item.id) !== String(option.value),
        ),
      );
      return;
    }
    onChange([
      ...values,
      {
        value: option.value,
        label: option.label,
        ...(option.data || {}),
      },
    ]);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`${SEARCH_SELECT_INPUT_CLASS} flex items-center justify-between text-left ${
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        }`}
        style={TRIGGER_RESET_STYLE}
      >
        <span
          className={`truncate ${values.length ? "text-gray-900" : "text-gray-500"}`}
        >
          {selectedLabel}
        </span>
        <FaChevronDown
          size={10}
          className={`ml-2 shrink-0 text-gray-400 transition ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && !disabled && (
        <div
          className={`counseling-filter-menu absolute z-[200] mt-1 w-full overflow-hidden rounded-lg bg-white`}
          style={{
            border: "1px solid #e5e7eb",
            boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
          }}
        >
          <div
            className="p-2"
            style={{
              borderBottom: "1px solid #f3f4f6",
              backgroundColor: "#fafbfc",
            }}
          >
            <div className="relative">
              <FaSearch
                className="pointer-events-none text-gray-400"
                size={11}
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                }}
              />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-md border border-gray-300 bg-white py-1.5 text-xs text-gray-900 focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
                style={{
                  paddingLeft: "30px",
                  paddingRight: "8px",
                  boxShadow: "none",
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <ul
            className="max-h-44 overflow-y-auto"
            style={{ listStyle: "none", margin: 0, padding: "4px 0" }}
          >
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-center text-xs text-gray-400">
                {emptyLabel}
              </li>
            ) : (
              filteredOptions.map((option) => {
                const selected = selectedValues.includes(String(option.value));
                return (
                  <li key={option.value} style={{ margin: 0, padding: 0 }}>
                    <button
                      type="button"
                      onClick={() => toggleOption(option)}
                      className="w-full text-left text-xs transition"
                      style={{
                        border: "none",
                        outline: "none",
                        background: selected
                          ? "rgba(0, 123, 255, 0.08)"
                          : "transparent",
                        color: selected ? "#007BFF" : "#374151",
                        fontWeight: selected ? 600 : 400,
                        padding: "8px 12px",
                        cursor: "pointer",
                        boxShadow: "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!selected) {
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function FilterChip({ chip, onClear }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-[#007BFF]/20 bg-[rgba(0,123,255,0.06)] px-2.5 py-1 text-xs font-medium text-[#007BFF]">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#007BFF]/70">
        {chip.category}
      </span>
      <span className="truncate">{chip.label}</span>
      <button
        type="button"
        onClick={() => onClear(chip)}
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition hover:bg-[#007BFF] hover:text-white"
        style={FILTER_BUTTON_RESET}
        aria-label={`Remove ${chip.label}`}
      >
        <FaTimes size={8} />
      </button>
    </span>
  );
}

function FilterField({
  label,
  required = false,
  children,
  count,
  hint,
}) {
  return (
    <div className="relative min-w-0">
      <label className={LABEL_CLASS}>
        {label}
        {required ? (
          <span className="ml-1 normal-case tracking-normal text-[#007BFF]">*</span>
        ) : null}
        {typeof count === "number" && count > 0 ? (
          <span className="ml-1.5 normal-case tracking-normal text-gray-400">
            ({count})
          </span>
        ) : null}
      </label>
      {children}
      {hint ? <p className="mt-1 text-[10px] leading-snug text-slate-400">{hint}</p> : null}
    </div>
  );
}

function CounclingSelectionBar(props) {
  const selectedProviderIds = useMemo(
    () =>
      (props.defaultProvider || []).map((provider) =>
        String(provider.id ?? provider.value ?? provider.name),
      ),
    [props.defaultProvider],
  );

  const activeFilters = useMemo(
    () => ({
      country: props.defaultCountry || "",
      providerIds: selectedProviderIds,
      discipline: props.defaultDiscipline || "",
      level: props.defaultLevel || "",
      duration: props.defaultDuration || "",
      intakeId: props.defaultIntakeId || "",
      campus: props.defaultCampus || "",
      fee: props.defaultFee || "",
      prerequisite: props.defaultPrerequisit || "",
    }),
    [
      selectedProviderIds,
      props.defaultDiscipline,
      props.defaultLevel,
      props.defaultDuration,
      props.defaultIntakeId,
      props.defaultCampus,
      props.defaultFee,
      props.defaultPrerequisit,
      props.defaultCountry,
    ],
  );

  const filterOptions = useMemo(() => {
    if (props.apiFilterOptions) {
      return mapApiFilterOptions(props.apiFilterOptions, countries_code || []);
    }
    return buildCounselingFilterOptions({
      courses: props.facetCourses || [],
      providers: props.providerList || [],
      activeFilters,
      staticCountries: countries_code || [],
    });
  }, [
    props.apiFilterOptions,
    props.facetCourses,
    props.providerList,
    activeFilters,
  ]);

  const {
    countryOptions,
    providerOptions,
    courseOptions,
    disciplineOptions,
    levelOptions,
    durationOptions,
    intakeOptions,
    campusOptions,
    feeOptions,
    prerequisiteOptions,
  } = filterOptions;

  const selectedProviders = useMemo(
    () =>
      (props.defaultProvider || []).map((provider) => ({
        value: provider.id ?? provider.value ?? provider.name,
        label: provider.name,
        ...provider,
      })),
    [props.defaultProvider],
  );

  const selectedCourses = useMemo(
    () =>
      (Array.isArray(props.defaultCourse) ? props.defaultCourse : []).map(
        (course) => ({
          value: course.id ?? course.value,
          label:
            course.label ||
            [course.name, course.providername, course.country]
              .filter(Boolean)
              .join(" · "),
          ...course,
        }),
      ),
    [props.defaultCourse],
  );

  const selectedIntakeValue = useMemo(() => {
    if (props.defaultIntakeId) {
      const byId = intakeOptions.find(
        (opt) => String(opt.rawValue) === String(props.defaultIntakeId),
      );
      if (byId) return byId.value;
    }
    const byLabel = intakeOptions.find((opt) => opt.label === props.defaultIntake);
    return byLabel?.value || "";
  }, [intakeOptions, props.defaultIntakeId, props.defaultIntake]);

  const activeFilterChips = useMemo(
    () =>
      buildActiveFilterChips({
        fee: props.defaultFee,
        level: props.defaultLevel,
        prerequisite: props.defaultPrerequisit,
        discipline: props.defaultDiscipline,
        country: props.defaultCountry,
        intake: props.defaultIntake,
        duration: props.defaultDuration,
        campus: props.defaultCampus,
        providers: props.defaultProvider,
        courses: Array.isArray(props.defaultCourse) ? props.defaultCourse : [],
      }),
    [
      props.defaultFee,
      props.defaultLevel,
      props.defaultPrerequisit,
      props.defaultDiscipline,
      props.defaultCountry,
      props.defaultIntake,
      props.defaultDuration,
      props.defaultCampus,
      props.defaultProvider,
      props.defaultCourse,
    ],
  );

  const facetLoading = props.facetLoading;

  const dropdownProps = {
    allowDeselect: true,
    clearable: true,
    loading: facetLoading,
    loadingLabel: "Loading...",
    inputClass: SEARCH_SELECT_INPUT_CLASS,
    disabled: facetLoading,
    menuClassName: COUNSELING_MENU_CLASS,
  };

  const hasActiveFilters = activeFilterChips.length > 0;

  const handleProviderChange = (selected) => {
    const mapped = (selected || []).map((item) => item.data || item);
    props.parentCallback(mapped, "provider", "");
  };

  const handleCourseChange = (selected) => {
    const mapped = (selected || []).map((item) => ({
      id: item.data?.id ?? item.value,
      value: item.data?.id ?? item.value,
      name: item.data?.name || item.name,
      label: item.label,
      providername: item.data?.providername,
      providerid: item.data?.providerid,
      country: item.data?.country,
      ...(item.data || {}),
    }));
    props.parentCallback(mapped, "course", "");
  };

  const removeProvider = (providerValue) => {
    const next = (props.defaultProvider || []).filter(
      (item) => String(item.id ?? item.value ?? item.name) !== String(providerValue),
    );
    props.parentCallback(next, "provider", "");
  };

  const removeCourse = (courseValue) => {
    const next = (Array.isArray(props.defaultCourse) ? props.defaultCourse : []).filter(
      (item) => String(item.id ?? item.value ?? "") !== String(courseValue),
    );
    props.parentCallback(next, "course", "");
  };

  const handleChipClear = (chip) => {
    const clear = chip.onClear;
    if (clear.type === "remove-provider") {
      removeProvider(clear.value);
      return;
    }
    if (clear.type === "remove-course") {
      removeCourse(clear.value);
      return;
    }
    if (clear.name === "intake") {
      props.parentCallback(clear.value, "intake", clear.optionName || "");
      return;
    }
    props.parentCallback(clear.value, clear.name);
  };

  const countrySelected = Boolean(props.defaultCountry);
  const providerSelected = (props.defaultProvider || []).length > 0;

  const noOptionsHint = facetLoading ? "Loading options..." : "No matches for current filters";

  return (
    <div className="counseling-filters-card mb-4 rounded-2xl border border-slate-200 bg-[#f4f7fb] p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="m-0 text-[15px] font-bold text-slate-900">Search filters</h2>
        {!props.resultsLoading && props.resultCount > 0 ? (
          <span className="shrink-0 rounded-full bg-[#007BFF] px-2.5 py-1 text-[11px] font-bold text-white">
            {props.resultCount.toLocaleString()} match
            {props.resultCount === 1 ? "" : "es"}
          </span>
        ) : props.resultsLoading ? (
          <span className="shrink-0 rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-500">
            Searching...
          </span>
        ) : null}
      </div>

      {activeFilterChips.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {activeFilterChips.map((chip) => (
            <FilterChip key={chip.key} chip={chip} onClear={handleChipClear} />
          ))}
        </div>
      ) : null}

      <div className="flex flex-col gap-2.5">
        <div className={ROW_CLASS}>
          <FilterField label="Country">
            <SearchSelectDropdown
              {...dropdownProps}
              value={props.defaultCountry || ""}
              onChange={(value) => props.parentCallback(value || "", "countries")}
              options={countryOptions}
              placeholder="All countries"
              searchPlaceholder="Search countries..."
              emptyLabel={noOptionsHint}
            />
          </FilterField>

          <FilterField
            label="Provider"
            hint={
              countrySelected
                ? "Providers in selected country"
                : "Select a country to narrow providers"
            }
          >
            <MultiSearchSelectDropdown
              values={selectedProviders}
              onChange={handleProviderChange}
              options={providerOptions}
              placeholder={countrySelected ? "Providers in country" : "All providers"}
              searchPlaceholder="Search providers..."
              disabled={facetLoading}
              emptyLabel={noOptionsHint}
            />
          </FilterField>

          <FilterField
            label="Course"
            hint={
              providerSelected
                ? "Courses offered by selected provider(s)"
                : countrySelected
                  ? "Courses available in selected country"
                  : "Select country or provider to narrow courses"
            }
          >
            <MultiSearchSelectDropdown
              values={selectedCourses}
              onChange={handleCourseChange}
              options={courseOptions}
              placeholder="All courses"
              searchPlaceholder="Search courses..."
              disabled={facetLoading}
              emptyLabel={noOptionsHint}
            />
          </FilterField>

          <FilterField label="Budget">
            <SearchSelectDropdown
              {...dropdownProps}
              value={props.defaultFee || ""}
              onChange={(value) => props.parentCallback(value || "", "tutionfee")}
              options={feeOptions}
              placeholder="Any budget"
              searchPlaceholder="Search budget..."
              emptyLabel="No budget ranges"
            />
          </FilterField>
        </div>

        <div className={ROW_CLASS}>
          <FilterField label="Degree / level">
            <SearchSelectDropdown
              {...dropdownProps}
              value={props.defaultLevel || ""}
              onChange={(value) => props.parentCallback(value || "", "levels")}
              options={levelOptions}
              placeholder="Any level"
              searchPlaceholder="Search levels..."
              emptyLabel={noOptionsHint}
            />
          </FilterField>

          <FilterField label="English Test">
            <SearchSelectDropdown
              {...dropdownProps}
              value={props.defaultPrerequisit || ""}
              onChange={(value) => props.parentCallback(value || "", "prerequisit")}
              options={prerequisiteOptions}
              placeholder="Any English test"
              searchPlaceholder="Search English tests..."
              emptyLabel={noOptionsHint}
            />
          </FilterField>

          <FilterField label="Discipline">
            <SearchSelectDropdown
              {...dropdownProps}
              value={props.defaultDiscipline || ""}
              onChange={(value) => props.parentCallback(value || "", "discipline")}
              options={disciplineOptions}
              placeholder="Any discipline"
              searchPlaceholder="Search disciplines..."
              emptyLabel={noOptionsHint}
            />
          </FilterField>

          <FilterField label="Intake">
            <SearchSelectDropdown
              {...dropdownProps}
              value={selectedIntakeValue}
              onChange={(value) => {
                const option = intakeOptions.find((item) => item.value === value);
                props.parentCallback(
                  option?.rawValue || "",
                  "intake",
                  option?.label || "",
                );
              }}
              options={intakeOptions}
              placeholder="Any intake"
              searchPlaceholder="Search intakes..."
              emptyLabel={noOptionsHint}
            />
          </FilterField>
        </div>

        <div className={ROW_CLASS}>
          <FilterField label="Duration">
            <SearchSelectDropdown
              {...dropdownProps}
              value={props.defaultDuration || ""}
              onChange={(value) => props.parentCallback(value || "", "duration")}
              options={durationOptions}
              placeholder="Any duration"
              searchPlaceholder="Search durations..."
              emptyLabel={noOptionsHint}
            />
          </FilterField>

          <FilterField label="Campus / city">
            <SearchSelectDropdown
              {...dropdownProps}
              value={props.defaultCampus || ""}
              onChange={(value) => props.parentCallback(value || "", "campus")}
              options={campusOptions}
              placeholder="All campuses"
              searchPlaceholder="Search campuses..."
              emptyLabel={noOptionsHint}
            />
          </FilterField>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={props?.handleReset}
          className={`inline-flex min-h-[40px] items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 ${
            hasActiveFilters
              ? "bg-[#007BFF] hover:bg-[#0069D9] hover:shadow"
              : "cursor-not-allowed bg-[#007BFF]/40"
          }`}
          style={FILTER_BUTTON_RESET}
          disabled={!hasActiveFilters}
        >
          <FaTimes size={12} />
          Clear all
        </button>
      </div>
    </div>
  );
}

export default CounclingSelectionBar;
